// Port of ios/Sources/ViewModels/RelativeTime.swift.
//
// The channel list's trailing timestamp: "17:22" today, "ayer" yesterday, "lun" within the
// last week, "6 ago" beyond that. A decision-for-decision port of Android's `RelativeTime`,
// tests included.
//
// Pure and fully parameterised on `now`/`timeZone`/`locale` so it can be unit tested: a
// formatter that reads the wall clock internally can only be tested by changing the machine's
// clock, which means in practice it never gets tested at all. The boundary cases are genuinely
// easy to get wrong — "yesterday" is a CALENDAR-DAY boundary, not "24 hours ago", and
// 23:59 → 00:01 is six minutes apart but still "ayer".
import { calendarDaysBetween, civil, dateFormatter } from './time';
import { locale as uiLocale } from '../i18n/i18n';

/**
 * Labels are injected rather than looked up from `Strings` so this stays pure.
 *
 * `today` is only consulted by `formatWithTime`; `format` never needs it, because a bare
 * "17:22" already means today in a list where every other row carries a day. It defaults to
 * empty so the existing call sites — none of which want the word — are untouched.
 */
export interface Labels {
  yesterday: string;
  today?: string;
}

/** `UiLocale.isSpanish`: the identifier starts with "es" ("es_ES", "es-MX", …). */
function isSpanish(locale: string): boolean {
  return locale.startsWith('es');
}

// WEB: Spanish abbreviations come from these tables, not from Intl. The Apple client formats
// with `es_ES` (`UiLocale.spanish`), whose CLDR data says "sept" and "mié"; the web's Spanish
// locale is `es-MX`, whose Intl data says "sep" — and older browser ICU builds add trailing
// dots or differ again. A fixed table produces the Swift result in every browser.
const SPANISH_WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const SPANISH_MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** "HH:mm" — 24-hour, zero-padded, whatever the locale (the Swift pattern is fixed). */
function clock(ms: number, timeZone: string | undefined): string {
  const c = civil(ms, timeZone);
  return pad2(c.hour) + ':' + pad2(c.minute);
}

/** "EEE", lowercased, trailing dots dropped. */
function weekday(ms: number, timeZone: string | undefined, locale: string): string {
  let w: string;
  if (isSpanish(locale)) {
    w = SPANISH_WEEKDAYS[civil(ms, timeZone).weekday];
  } else {
    w = dateFormatter(locale, { weekday: 'short' }, timeZone).format(new Date(ms));
  }
  w = w.toLocaleLowerCase(locale);
  while (w.endsWith('.')) w = w.slice(0, -1);
  return w;
}

/** "d MMM" in Spanish, "MMM d" otherwise — dots removed, lowercased. */
function dayMonth(ms: number, timeZone: string | undefined, locale: string): string {
  const c = civil(ms, timeZone);
  let out: string;
  if (isSpanish(locale)) {
    out = `${c.day} ${SPANISH_MONTHS[c.month - 1]}`;
  } else {
    const month =
      dateFormatter(locale, { month: 'short' }, timeZone)
        .formatToParts(new Date(ms))
        .find((p) => p.type === 'month')?.value ?? '';
    out = `${month} ${c.day}`;
  }
  return out.split('.').join('').toLocaleLowerCase(locale);
}

export function format(
  epochMillis: number,
  now: number,
  labels: Labels,
  timeZone?: string,
  locale: string = uiLocale(),
): string {
  // A channel that has never had a message carries 0 here; there is no sensible relative
  // time for "never", and rendering "1 ene" (the epoch) would be a lie.
  if (!(epochMillis > 0)) return '';

  // Whole calendar days from `then` to `now`, ignoring the time of day. Deliberately NOT
  // `(now - then) / 86_400_000`: that answers "how many 24-hour periods", which disagrees
  // with what a person means by "yesterday" for most of every day, and is wrong across DST.
  const daysApart = calendarDaysBetween(epochMillis, now, timeZone);

  if (daysApart === 0) return clock(epochMillis, timeZone);
  if (daysApart === 1) return labels.yesterday;
  // Strictly less than 7 so "the same weekday last week" can never be shown as if it were
  // this week — 7 days back reads as "lun" and means something else entirely.
  if (daysApart >= 2 && daysApart <= 6) return weekday(epochMillis, timeZone, locale);
  // Future timestamps (clock skew between this device and the server) are shown as a time
  // rather than a negative-day weekday.
  if (daysApart < 0) return clock(epochMillis, timeZone);
  return dayMonth(epochMillis, timeZone, locale);
}

/**
 * Both halves: "hoy 17:22", "ayer 09:14", "lun 11:40", "6 sep 08:05".
 *
 * `format` deliberately shows only ONE of the two — it fills a narrow trailing column on a
 * row whose message is right there, so the day is what matters and the clock time is noise.
 * This variant exists for the places that ask the reader to COMPARE two timestamps against
 * each other, where one is the only thing separating two otherwise similar-looking choices.
 * There, "ayer" against "17:22" is not comparable at a glance: one is a day, the other a
 * clock time, and deciding which is newer means first working out that "17:22" even implies
 * today.
 *
 * The day part is dropped entirely when `Labels.today` is empty, rather than printing a
 * stray space — a caller that did not supply the word gets exactly what `format` would give.
 */
export function formatWithTime(
  epochMillis: number,
  now: number,
  labels: Labels,
  timeZone?: string,
  locale: string = uiLocale(),
): string {
  if (!(epochMillis > 0)) return '';
  const time = clock(epochMillis, timeZone);
  const today = labels.today ?? '';
  const daysApart = calendarDaysBetween(epochMillis, now, timeZone);

  let day: string;
  if (daysApart === 0) day = today;
  else if (daysApart === 1) day = labels.yesterday;
  else if (daysApart >= 2 && daysApart <= 6) day = weekday(epochMillis, timeZone, locale);
  // A future stamp is clock skew, not a real date: say "today" rather than inventing a day
  // that has not happened yet.
  else if (daysApart < 0) day = today;
  else day = dayMonth(epochMillis, timeZone, locale);

  return day === '' ? time : `${day} ${time}`;
}

/** Namespace form, for `RelativeTime.format(...)` call sites. */
export const RelativeTime = { format, formatWithTime } as const;
